import { useEffect, useState } from "react";
import api from "../services/api";
import { Button } from "../components/ui/button";
import { Send, Loader2, ShieldCheck, AlertTriangle, Megaphone, Eye, RefreshCw, Power } from "lucide-react";
import { toast } from "sonner";

const SEGMENT_OPTIONS = [
  { id: "all", label: "All customers" },
  { id: "dormant_30d", label: "Dormant (no order 30d+)" },
  { id: "new_7d", label: "New signups (7d)" },
  { id: "silver_tier", label: "Silver tier members" },
  { id: "gold_tier", label: "Gold tier members" },
];

export default function AdminCampaigns() {
  const [templates, setTemplates] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [segment, setSegment] = useState("dormant_30d");
  const [templateId, setTemplateId] = useState("comeback_20");
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [sending, setSending] = useState(false);
  const [testPhones, setTestPhones] = useState("");
  const [winback, setWinback] = useState({ live_send_enabled: false, last_runs: [], quiet_hours_now: false });
  const [winbackRunning, setWinbackRunning] = useState(false);

  const loadAll = async () => {
    try {
      const [t, c, w] = await Promise.all([
        api.get("/admin/campaigns/templates"),
        api.get("/admin/campaigns/"),
        api.get("/admin/campaigns/winback/status"),
      ]);
      setTemplates(t.data?.templates || []);
      setCampaigns(c.data?.campaigns || []);
      setWinback(w.data);
    } catch (e) {
      toast.error("Failed to load campaigns");
    }
  };

  useEffect(() => { loadAll(); }, []);

  const runPreview = async () => {
    setPreviewing(true);
    try {
      const { data } = await api.get(`/admin/campaigns/segments/${segment}/preview`);
      setPreview(data);
    } catch (e) {
      toast.error("Preview failed");
    } finally { setPreviewing(false); }
  };

  const sendCampaign = async (dryRun) => {
    setSending(true);
    try {
      const body = { segment, template_id: templateId, dry_run: dryRun };
      const phones = testPhones.split(",").map((p) => p.trim()).filter(Boolean);
      if (phones.length) body.test_phones = phones;
      const { data } = await api.post("/admin/campaigns/send", body);
      toast.success(dryRun ? `Dry-run complete · would reach ${data.total_filtered}` : `Sent ${data.sent}, failed ${data.failed}`);
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setSending(false); }
  };

  const toggleWinback = async () => {
    try {
      const { data } = await api.post("/admin/campaigns/winback/toggle", { live: !winback.live_send_enabled });
      setWinback((w) => ({ ...w, live_send_enabled: data.live_send_enabled }));
      toast.success(data.live_send_enabled ? "Win-back LIVE sends enabled" : "Win-back back to dry-run only");
    } catch {
      toast.error("Toggle failed");
    }
  };

  const runWinbackNow = async () => {
    setWinbackRunning(true);
    try {
      const { data } = await api.post("/admin/campaigns/winback/run-now");
      toast.success(`Win-back ran · dry_run=${data.dry_run} · filtered ${data.filtered}`);
      loadAll();
    } catch {
      toast.error("Win-back run failed");
    } finally { setWinbackRunning(false); }
  };

  const tmpl = templates.find((t) => t.id === templateId);

  return (
    <div className="space-y-6 max-w-5xl mx-auto" data-testid="admin-campaigns-page">
      <div className="animate-fadeInUp flex items-center justify-between gap-4">
        <div>
          <h1 className="font-['Outfit'] text-2xl sm:text-3xl font-bold text-slate-900 flex items-center gap-2">
            <Megaphone className="h-7 w-7 text-[#FF6B00]" /> WhatsApp Campaigns
          </h1>
          <p className="text-sm text-slate-500 mt-1">Segment, preview, and (carefully) send marketing blasts.</p>
        </div>
        <Button variant="outline" onClick={loadAll} className="gap-2" data-testid="campaigns-refresh-btn">
          <RefreshCw className="h-4 w-4" /> Refresh
        </Button>
      </div>

      {/* Safety banner */}
      <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-4 flex items-start gap-3" data-testid="safety-banner">
        <ShieldCheck className="h-5 w-5 text-emerald-600 mt-0.5 shrink-0" />
        <div className="text-xs text-slate-700 leading-relaxed">
          Quiet hours <b>22:00–07:00 IST</b> block all live sends. Recipients on the DND list are auto-filtered.
          Anyone with 3+ failed sends in 30 days is paused. <b>Dry-run is on by default</b> — flip it deliberately to send for real.
        </div>
      </div>

      {/* Composer */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 md:p-6 animate-fadeInUp" data-testid="campaign-composer">
        <h2 className="font-['Outfit'] text-base font-semibold text-slate-900 mb-4">New campaign</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Segment</label>
            <select
              value={segment}
              onChange={(e) => { setSegment(e.target.value); setPreview(null); }}
              className="w-full mt-1.5 px-3 py-2 rounded-xl border border-slate-200 text-sm"
              data-testid="campaign-segment-select"
            >
              {SEGMENT_OPTIONS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Template</label>
            <select
              value={templateId}
              onChange={(e) => setTemplateId(e.target.value)}
              className="w-full mt-1.5 px-3 py-2 rounded-xl border border-slate-200 text-sm"
              data-testid="campaign-template-select"
            >
              {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
        </div>

        {tmpl && (
          <div className="mt-4 bg-slate-50 rounded-xl p-3 text-xs text-slate-700 whitespace-pre-wrap" data-testid="template-preview">
            {tmpl.body}
          </div>
        )}

        <div className="mt-4">
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Test phones (optional, comma-separated)</label>
          <input
            type="text"
            placeholder="+919876543210, +919999999999"
            value={testPhones}
            onChange={(e) => setTestPhones(e.target.value)}
            className="w-full mt-1.5 px-3 py-2 rounded-xl border border-slate-200 text-sm"
            data-testid="campaign-test-phones"
          />
          <p className="text-[10px] text-slate-400 mt-1">When provided, overrides the segment — useful for verifying the message before going live.</p>
        </div>

        <div className="mt-5 flex flex-wrap gap-2">
          <Button onClick={runPreview} disabled={previewing} variant="outline" className="gap-2" data-testid="campaign-preview-btn">
            {previewing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />} Preview recipients
          </Button>
          <Button onClick={() => sendCampaign(true)} disabled={sending} className="bg-slate-900 hover:bg-slate-800 text-white gap-2" data-testid="campaign-dryrun-btn">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />} Dry-run
          </Button>
          <Button
            onClick={() => {
              if (!window.confirm("Send for REAL? This will fire WhatsApp messages to live users. Continue?")) return;
              sendCampaign(false);
            }}
            disabled={sending}
            className="bg-[#FF6B00] hover:bg-orange-600 text-white gap-2"
            data-testid="campaign-send-live-btn"
          >
            <Send className="h-4 w-4" /> Send live
          </Button>
        </div>

        {preview && (
          <div className="mt-4 bg-blue-50 border border-blue-100 rounded-xl p-3 text-xs text-slate-700" data-testid="campaign-preview-result">
            Segment <b>{preview.segment}</b> → <b>{preview.total}</b> total recipients
          </div>
        )}
      </div>

      {/* Win-back automation */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 md:p-6 animate-fadeInUp" data-testid="winback-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="font-['Outfit'] text-base font-semibold text-slate-900 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-500" /> Daily Win-back Automation
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Runs at 10:00 IST daily. Sends "COMEBACK20" to anyone dormant for 30d+.
              Currently <b className={winback.live_send_enabled ? "text-emerald-700" : "text-amber-700"}>
                {winback.live_send_enabled ? "LIVE" : "DRY-RUN ONLY"}
              </b>.
              {winback.quiet_hours_now ? " · Quiet hours active right now." : ""}
            </p>
          </div>
          <div className="flex gap-2">
            <Button onClick={runWinbackNow} disabled={winbackRunning} variant="outline" className="gap-2" data-testid="winback-run-btn">
              {winbackRunning ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Run now
            </Button>
            <Button onClick={toggleWinback} className={`${winback.live_send_enabled ? "bg-emerald-600 hover:bg-emerald-700" : "bg-slate-700 hover:bg-slate-800"} text-white gap-2`} data-testid="winback-toggle-btn">
              <Power className="h-4 w-4" /> {winback.live_send_enabled ? "Live · click to pause" : "Dry-run · click to go live"}
            </Button>
          </div>
        </div>
        {!!winback.last_runs?.length && (
          <div className="mt-4 border-t border-slate-100 pt-3">
            <p className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mb-2">Last 10 runs</p>
            <ul className="text-xs space-y-1.5">
              {winback.last_runs.map((r, i) => (
                <li key={i} className="flex justify-between text-slate-600">
                  <span>{new Date(r.ts || r.ran_at).toLocaleString()}</span>
                  <span>targeted {r.targeted} · filtered {r.filtered} · sent {r.sent}{r.dry_run ? " (dry-run)" : ""}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* History */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 animate-fadeInUp" data-testid="campaign-history">
        <div className="p-5 border-b border-slate-100">
          <h2 className="font-['Outfit'] text-base font-semibold text-slate-900">Past campaigns</h2>
        </div>
        {!campaigns.length ? (
          <div className="p-10 text-center">
            <p className="text-sm text-slate-500">No campaigns yet. Run a dry-run above to start.</p>
          </div>
        ) : (
          <ul className="divide-y divide-slate-50">
            {campaigns.map((c) => (
              <li key={c.id} className="px-5 py-3 flex items-center justify-between text-sm" data-testid={`campaign-row-${c.id}`}>
                <div>
                  <p className="font-semibold text-slate-900">{c.template_id} → {c.segment}</p>
                  <p className="text-xs text-slate-400">
                    {new Date(c.created_at).toLocaleString()} · by {c.created_by_email || "—"}
                  </p>
                </div>
                <div className="text-right">
                  <p className={`text-xs font-semibold ${c.dry_run ? "text-slate-500" : "text-emerald-700"}`}>
                    {c.dry_run ? "DRY-RUN" : c.status?.toUpperCase()}
                  </p>
                  <p className="text-xs text-slate-500">
                    targeted {c.total_targeted} · sent {c.sent_count} · failed {c.failed_count}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
